<script lang="ts" setup>
import { YandexMapMarker } from 'vue-yandex-maps';
import { DELIVERY_SERVICES } from '~/constants/deliveryServices';
import { PAYMENT_OPTIONS } from '~/constants/paymentOptions';
import type { DeliveryService, PickupPoint } from '~/types/Checkout';

const props = defineProps<{
  pickupPoint: PickupPoint;
  isShowPrice: boolean;
}>();

const route = useRoute();
const checkoutStore = useCheckoutStore();
const { openMarker } = storeToRefs(checkoutStore);
const deliveryServices = ref(DELIVERY_SERVICES);

const isShowModal = ref(false);

const isPriceLoading = ref(false);
const preciseDeliveryPrice = ref<number | null>(null);

watch(
  () => openMarker.value,
  async () => {
    if (
      openMarker.value?.code === props.pickupPoint.code &&
      props.pickupPoint.delivery_provider === DELIVERY_SERVICES.boxberry.slug &&
      !preciseDeliveryPrice.value
    ) {
      isPriceLoading.value = true;

      const response = await checkoutStore.getPickupPointDeliveryCost(props.pickupPoint.code);
      if (response) preciseDeliveryPrice.value = response.delivery_price;

      isPriceLoading.value = false;
    }
  },
  { immediate: true },
);

const deliveryPrice = computed(() => {
  if (preciseDeliveryPrice.value) {
    return preciseDeliveryPrice.value === 0 ? 'Бесплатно' : `${preciseDeliveryPrice.value.toLocaleString('ru-RU')} ₽`;
  }

  if (typeof props.pickupPoint.delivery_price === 'undefined') return null;

  return props.pickupPoint.delivery_price === 0
    ? 'Бесплатно'
    : `${
        props.pickupPoint.delivery_provider === DELIVERY_SERVICES.boxberry.slug ? 'От ' : ''
      }${props.pickupPoint.delivery_price.toLocaleString('ru-RU')} ₽`;
});

const isMarkerOpen = computed(() => openMarker.value?.code === props.pickupPoint.code);

function selectPoint() {
  if (props.pickupPoint.delivery_provider !== DELIVERY_SERVICES.boxberry.slug) {
    checkoutStore.selectedPaymentOption = checkoutStore.paymentsAndDeliveries.online;
  }

  checkoutStore.deliveryCost = preciseDeliveryPrice.value ?? props.pickupPoint.delivery_price;
  checkoutStore.selectedPickupPoint = props.pickupPoint;
  checkoutStore.selectedDeliveryService = DELIVERY_SERVICES[props.pickupPoint.delivery_provider] as DeliveryService;
  checkoutStore.selectedDeliveryType = checkoutStore.selectedPaymentOption.deliveryTypes.selfPickup;

  checkoutStore.openMarker = null;
  checkoutStore.deliverySwitches.selfPickup = false;
}

function handleConfirm() {
  if (
    checkoutStore.selectedPaymentOption.slug === PAYMENT_OPTIONS.cash_upon_receipt &&
    props.pickupPoint.delivery_provider !== DELIVERY_SERVICES.boxberry.slug
  ) {
    isShowModal.value = true;
  } else {
    selectPoint();
  }
}

function handleModalClose() {
  isShowModal.value = false;
  openMarker.value = null;
}
</script>

<template>
  <YandexMapMarker
    :class="{ '-translate-y-3': isMarkerOpen }"
    :settings="{
      coordinates: [Number(pickupPoint.coord_x), Number(pickupPoint.coord_y)],
      onClick: () => (openMarker = pickupPoint),
      zIndex: openMarker?.code === pickupPoint.code ? 1 : 0,
      hideOutsideViewport: true,
    }"
    class="transition"
  >
    <div class="relative -top-8 -left-5">
      <component
        :is="`svgo-delivery-pin-${pickupPoint.delivery_provider ?? 'shop'}${isMarkerOpen ? '-selected' : ''}`"
        :font-controlled="false"
        class="size-10 cursor-pointer"
        filled
      />

      <div
        v-if="isMarkerOpen"
        class="ym-marker__balloon"
      >
        <SvgoCross
          :font-controlled="false"
          class="w-6 h-6 absolute top-3 right-3 cursor-pointer"
          filled
          @click.stop="openMarker = null"
        />

        <div
          v-if="pickupPoint.delivery_provider"
          class="flex items-center gap-2"
        >
          <component
            :is="`svgo-delivery-${pickupPoint.delivery_provider}`"
            :font-controlled="false"
            class="w-6 h-6"
            filled
          />

          <h3 class="flex-grow">
            {{ deliveryServices[pickupPoint.delivery_provider].title }}
          </h3>
        </div>

        <p>{{ pickupPoint.address }}</p>

        <p v-if="pickupPoint.delivery_provider">
          Дата доставки –
          <span class="font-bold">
            {{ getDeliveryDates(pickupPoint.delivery_period_min, pickupPoint.delivery_period_max) }}
          </span>
        </p>

        <p v-if="deliveryPrice">
          Стоимость –
          <span
            :class="{ 'text-hell-green': pickupPoint.delivery_price === 0 }"
            class="font-bold"
          >
            {{ deliveryPrice }}

            <Transition
              v-if="isPriceLoading"
              name="loader"
            >
              <SvgoSpinner
                class="inline animate-spin"
                title="Узнаем точную стоимость доставки..."
              />
            </Transition>
          </span>
        </p>

        <TheBtn
          v-if="route.name === 'checkout'"
          class="mt-2"
          size="md"
          @click="handleConfirm"
        >
          Заберу отсюда
        </TheBtn>

        <div class="ym-marker__balloon-pointer" />
      </div>
    </div>

    <div
      v-if="isShowPrice && deliveryPrice !== null"
      class="ym-marker__delivery-price-box"
    >
      <div
        :class="{ 'ym-marker__delivery-price--free': pickupPoint.delivery_price === 0 }"
        class="ym-marker__delivery-price"
      >
        {{ deliveryPrice }}
      </div>
    </div>

    <BaseModal
      v-model="isShowModal"
      cancel-btn-text="Отклонить"
      confirm-btn-text="Принять"
      title="Необходима оплата онлайн"
      @cancel="handleModalClose"
      @close="handleModalClose"
      @confirm="selectPoint"
    >
      <p class="text-sm">
        В пунктах СДЭК отсутствует возможность оплаты при получении. Если выберете этот пункт выдачи, способ оплаты
        станет "Онлайн"
      </p>
    </BaseModal>
  </YandexMapMarker>
</template>

<style lang="scss" scoped>
.ym-marker {
  &__balloon {
    position: absolute;
    top: 120%;
    left: -330%;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    width: 300px;
    padding: 12px;
    border-radius: 12px;
    background: $white;
    color: $hell-black;
    box-shadow: 0 0 5px 0 rgb(0 0 0 / 20%);
  }

  &__balloon-pointer {
    position: absolute;
    top: -8px;
    left: 144px;
    width: 16px;
    height: 16px;
    background-color: $white;
    box-shadow: -3px -2px 3px 0 rgb(0 0 0 / 20%);
    transform: rotate(45deg);
  }

  &__delivery-price-box {
    position: absolute;
    top: -140%;
    left: -115%;
    display: flex;
    justify-content: center;
    width: 92px;
  }

  &__delivery-price {
    width: fit-content;
    padding: 4px 6px;
    border: 1px solid $hell-black-20;
    border-radius: 4px;
    background-color: $white;
    color: $hell-black;
    font-weight: $bold;

    &--free {
      background-color: $hell-yellow;
      color: $white;
    }
  }
}
</style>
